import { assignRefs, cx } from "@jugl-web/utils";
import {
  FC,
  Fragment,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Tooltip } from "../../../primitives/Tooltip";
import { TableCell, TableCellProps } from "../TableCell/TableCell";

export type TableTagsCellProps = {
  tags: string[];
} & Pick<
  TableCellProps,
  | "classNames"
  | "clipboardContent"
  | "clipboardSuccessMessage"
  | "endContent"
  | "isFocused"
  | "isHoverable"
  | "isDisabled"
  | "onClick"
>;

const TAG_GAP = 6;
const RESERVED_WIDTH = 40;

export const TableTagsCell = forwardRef<HTMLDivElement, TableTagsCellProps>(
  ({ tags, classNames, ...tableCellProps }, ref) => {
    const [pills, setPills] = useState<{
      visible: Set<string>;
      invisible: Set<string>;
    }>({
      visible: new Set(),
      invisible: new Set(),
    });

    const hasOverflowingPills = pills.invisible.size > 0;
    const hasOnlyOverflowingPills =
      pills.visible.size === 0 && hasOverflowingPills;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const tagsMapRef = useRef<Map<string, HTMLDivElement>>(new Map());

    const calculateVisibleTags = useCallback(() => {
      if (!containerRef.current) return;
      if (!tagsMapRef.current.size) return;

      const containerWidth = containerRef.current.offsetWidth;

      let totalWidth = 0;
      const visible: string[] = [];
      const invisible: string[] = [];

      tags.forEach((tag) => {
        const tagElement = tagsMapRef.current.get(tag);

        if (tagElement) {
          const isOverflow =
            totalWidth + tagElement.offsetWidth + TAG_GAP + RESERVED_WIDTH >
            containerWidth;

          if (isOverflow) {
            invisible.push(tag);
          } else {
            visible.push(tag);
          }

          totalWidth += tagElement.offsetWidth + TAG_GAP;
        }
      });

      setPills({
        visible: new Set(visible),
        invisible: new Set(invisible),
      });
    }, [tags]);

    useLayoutEffect(() => {
      calculateVisibleTags();

      const container = containerRef.current;

      if (!container) return undefined;

      const resizeObserver = new ResizeObserver(() => calculateVisibleTags());

      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }, [calculateVisibleTags]);

    if (tags.length === 0) {
      return (
        <TableCell ref={ref} classNames={classNames} {...tableCellProps}>
          <span className="text-grey">-</span>
        </TableCell>
      );
    }

    const commonPillClassName =
      "bg-primary-50 text-dark h-7 items-center truncate rounded-lg px-2 text-[13px]";

    return (
      <Tooltip
        placement="bottom"
        renderTrigger={({ props: tooltipProps, ref: tooltipRef }) => (
          <TableCell
            ref={assignRefs([ref, tooltipRef])}
            classNames={{
              ...classNames,
              // Inject overflow-hidden class to avoid layout shift when tags are hidden
              cell: cx("overflow-hidden", classNames?.cell),
            }}
            {...tableCellProps}
            {...tooltipProps}
          >
            <div
              ref={containerRef}
              className="font-secondary flex w-full items-center"
              style={{ gap: TAG_GAP }}
            >
              {tags.length > 1 ? (
                <>
                  {tags.map((pill, index) => {
                    const isLastVisiblePill = index === pills.visible.size - 1;

                    return (
                      <Fragment key={+index}>
                        {hasOnlyOverflowingPills && (
                          <MoreTagsPill count={pills.invisible.size} />
                        )}
                        <div
                          ref={(el) => {
                            if (el) {
                              tagsMapRef.current.set(pill, el);
                            } else {
                              tagsMapRef.current.delete(pill);
                            }
                          }}
                          className={cx(
                            commonPillClassName,
                            "flex min-w-min",
                            pills.visible.has(pill) ? "visible" : "invisible"
                          )}
                        >
                          {pill}
                        </div>
                        {isLastVisiblePill && hasOverflowingPills && (
                          <MoreTagsPill count={pills.invisible.size} />
                        )}
                      </Fragment>
                    );
                  })}
                </>
              ) : (
                <div
                  className={cx(commonPillClassName, "inline-flex max-w-full")}
                >
                  <span className="truncate">{tags[0]}</span>
                </div>
              )}
            </div>
          </TableCell>
        )}
      >
        <ul className="m-0 list-disc pl-4">
          {tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
      </Tooltip>
    );
  }
);

interface MoreTagsPillProps {
  count: number;
}

const MoreTagsPill: FC<MoreTagsPillProps> = ({ count }) => (
  <div className="bg-primary-700 flex h-7 items-center whitespace-nowrap rounded-lg px-2 text-sm text-white">
    +{count}
  </div>
);
